import React, { lazy, Suspense, useMemo } from 'react'
import { AuthRoute, NotFound, Route, Switch, Redirect, useParams, useLocation } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useUser } from '@fs/zion-user'
import { NoticeLoading } from '@fs/zion-icon'
import { useCanAddPersonalData } from '@fs/zion-permissions'

import sites from './sites'
import { MemoryBookProvider } from './providers/MemoryBookProvider'
import { ChineseSurnameProvider } from './providers/ChineseSurnameProvider'

// Import site configs
import chineseConfig from './sites/chinese/chinese-config'
import PrintingPages from './pages/PrintingPages'

const EditorPage = lazy(() => import('./pages/EditorPage/EditorPage'))
const MediaBanners = lazy(() => import('@fs/zion-media-banners'))

// Wrapper component for EditorPage with providers
const WrappedEditorPage = (props) => (
  <ChineseSurnameProvider>
    <EditorPage {...props} />
  </ChineseSurnameProvider>
)

function WrappedPrintPage() {
  return (
    <ChineseSurnameProvider>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <PrintingPages />
      </div>
    </ChineseSurnameProvider>
  )
}

// Helper function to get config based on site name
const getSiteConfig = (siteName) => {
  if (siteName === 'chinese') {
    return chineseConfig
  }
  return chineseConfig
}

export default function App() {
  // The useCanAddPersonalData hook is used in conjuction with the requirePersonalData prop due to use wanting to show the landing page initially.
  const [canAddPersonalData] = useCanAddPersonalData()
  const location = useLocation()
  const { signedIn } = useUser()
  const canView = !signedIn || canAddPersonalData
  const { siteName } = useParams()
  const siteConfig = useMemo(() => getSiteConfig(siteName), [siteName])

  const siteNames = useMemo(() => Object.keys(sites).join('|'), [])
  const isLandingPage = useMemo(
    () => new RegExp(`^/(${siteNames})/?$`).test(location.pathname),
    [siteNames, location.pathname]
  )
  // EditorPage loads the book itself whenever the path addresses a specific book — skip the
  // provider's first-book fetch so the two loaders can't race (see SET_BOOK guard in the
  // reducer). Deliberately unanchored at the end so nested editor routes still match.
  const isEditorBookRoute = useMemo(
    () => new RegExp(`^/(${siteNames})/editor/[^/]+`).test(location.pathname),
    [siteNames, location.pathname]
  )

  return (
    <ErrorBoundary>
      <MemoryBookProvider siteConfig={siteConfig} isAuthenticated={signedIn} skipFirstBookFetch={isEditorBookRoute}>
        {isLandingPage && (
          <Suspense fallback={<NoticeLoading />}>
            <MediaBanners />
          </Suspense>
        )}
        <Suspense>
          <Switch>
            <Route exact path="/" requirePersonalData={!canView}>
              <Redirect to="/chinese" />
            </Route>
            <Route exact path="/editor" requirePersonalData={!canView}>
              <Redirect to="/chinese/editor" />
            </Route>
            {Object.entries(sites).map(([path]) => {
              return (
                <AuthRoute
                  key={path}
                  component={WrappedEditorPage}
                  exact
                  path={`/:siteName(${path})/editor/:bookId`}
                  requirePersonalData={!canView}
                />
              )
            })}
            {Object.entries(sites).map(([path, component]) => {
              return (
                <Route
                  key={path}
                  component={component}
                  exact
                  path={`/:siteName(${path})`}
                  requirePersonalData={!canView}
                />
              )
            })}

            {Object.entries(sites).map(([path]) => {
              return (
                <AuthRoute
                  key={path}
                  component={WrappedEditorPage}
                  exact
                  path={`/:siteName(${path})/editor`}
                  requirePersonalData={!canView}
                />
              )
            })}
            {Object.entries(sites).map(([path]) => {
              return (
                <AuthRoute
                  key={path}
                  component={WrappedPrintPage}
                  exact
                  path={`/:siteName(${path})/print`}
                  requirePersonalData={!canView}
                />
              )
            })}
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </MemoryBookProvider>
    </ErrorBoundary>
  )
}
